import {
  attributeCountFor,
  EQUIPMENT_MAX_LEVEL,
  type EquipmentRarity,
  QUANTUM_STABILIZER_ID,
} from './catalog';

/**
 * Raising a component's level: the chances, the risk, and what it costs.
 *
 * ## The chances are exact integers
 *
 * Basis points, never a float probability: 5% is `500`, and the draw is
 * `uniformBelow(10000) < 500` against a bias-free cryptographic source on the
 * server. A float would make "5%" mean something slightly different on two
 * machines, and the difference between 4,999% and 5% is somebody's item.
 *
 * ## What a failure costs
 *
 * Below level 6 a failure costs the payment and nothing else - the component
 * stays exactly where it was. From the attempt at level 6 onwards a failure
 * DESTROYS the component unless a Quantum Stabiliser is on the attempt. That
 * boundary is a single number here (`DESTRUCTION_FROM_TARGET_LEVEL`) rather
 * than a comparison written out at each call site, because a mistake in it is
 * somebody's Prismatic.
 *
 * ## Cash never buys luck
 *
 * The whole payment is in Gold or the whole payment is in Cash, never a mix.
 * The currency chosen changes NOTHING else: not the chance, not the risk, not
 * the draw, not the stabiliser, not the resulting level. It is an alternative
 * way to pay, and that is deliberately all it is.
 */

/** Version of the cost table. Stored on every attempt that is recorded. */
export const UPGRADE_COST_VERSION = 1;

/**
 * Chance of reaching each level, in basis points. Keyed by the TARGET level.
 *
 * Level 2 is certain; from there the curve falls to one attempt in twenty at
 * level 10. Together with the destruction rule this is what makes a level-10
 * component a rare object rather than a matter of patience.
 */
export const UPGRADE_SUCCESS_BPS: Readonly<Record<number, number>> = Object.freeze({
  2: 10_000,
  3: 9_000,
  4: 7_000,
  5: 5_000,
  6: 4_000,
  7: 2_000,
  8: 1_500,
  9: 1_000,
  10: 500,
});

/**
 * The first TARGET level whose failure destroys the component.
 *
 * Stated once. `targetLevel >= 6` is the whole rule: an attempt at level 5 is
 * safe, an attempt at level 6 is not, and there is no third case.
 */
export const DESTRUCTION_FROM_TARGET_LEVEL = 6;

/** Whether failing this attempt destroys the component (absent protection). */
export function attemptDestroysOnFailure(targetLevel: number): boolean {
  return targetLevel >= DESTRUCTION_FROM_TARGET_LEVEL;
}

/** Whether a Quantum Stabiliser does anything at all on this attempt. */
export function stabilizerIsUseful(targetLevel: number): boolean {
  return attemptDestroysOnFailure(targetLevel);
}

/**
 * Gold cost per TARGET level, PROVISIONAL.
 *
 * Anchored on what map 1 actually pays. A normal mob is worth half a Gold and
 * a boss sixty (`HUNT_BALANCE`), and an early player clearing roughly one mob
 * a second earns on the order of 30 Gold a minute - so the first upgrade is
 * about a quarter of an hour of play and the last is days of it. The curve is
 * steeper than the success curve is shallow, on purpose: the expected cost of
 * reaching level 10 has to be dominated by the attempts, not by one price.
 *
 * The twenty maps and the drop tables do not exist yet, so the Gold a player
 * will really earn at map 10 is unknown. This table is a starting point, it is
 * versioned, and it is listed in `docs/OPEN_QUESTIONS.md`.
 */
export const UPGRADE_GOLD_COST: Readonly<Record<number, bigint>> = Object.freeze({
  2: 500n,
  3: 1_200n,
  4: 3_000n,
  5: 7_500n,
  6: 18_000n,
  7: 45_000n,
  8: 110_000n,
  9: 260_000n,
  10: 600_000n,
});

/**
 * How much more a broader component costs to upgrade, in basis points.
 *
 * `10000 + 2500 x (attributes - 1)`: an Uncommon pays 1,00x and a Prismatic
 * 3,00x. This is the ONE place rarity changes a number other than the attribute
 * count, and it is documented rather than secret, because the reason is
 * arithmetic: one upgrade of a Prismatic raises nine attributes at once, so
 * charging it the same as an Uncommon would make breadth free. It is
 * deliberately sublinear - nine times the price would make a Prismatic
 * unupgradeable, and the point of a Prismatic is that it is worth upgrading.
 */
export function rarityCostMultiplierBps(rarity: EquipmentRarity): number {
  return 10_000 + 2_500 * (attributeCountFor(rarity) - 1);
}

/**
 * How much Gold one Cash is worth for this purpose, PROVISIONAL.
 *
 * There is no Gold-for-Cash exchange in KENOCYBER and this does not create one:
 * it is only the rate at which an upgrade may be paid for in the other
 * currency. A real rate needs the store's own numbers and the maps' earning
 * rates, neither of which is decided - see `docs/OPEN_QUESTIONS.md`.
 */
export const PROVISIONAL_GOLD_PER_CASH = 5_000n;

/** The two ways to pay. Never mixed inside one attempt. */
export const UPGRADE_CURRENCIES = ['gold', 'cash'] as const;
export type UpgradeCurrency = (typeof UPGRADE_CURRENCIES)[number];

export interface UpgradeQuote {
  readonly currentLevel: number;
  readonly targetLevel: number;
  readonly successBps: number;
  /** True when a failure destroys the component and no stabiliser is used. */
  readonly destroysOnFailure: boolean;
  /** The consumable that prevents that destruction, or null when useless. */
  readonly stabilizerDefinitionId: string | null;
  readonly goldCost: bigint;
  readonly cashCost: bigint;
  readonly rarityMultiplierBps: number;
  readonly costVersion: number;
}

export class UpgradeNotAvailableError extends RangeError {
  constructor(detail: string) {
    super(`equipment upgrade unavailable: ${detail}`);
    this.name = 'UpgradeNotAvailableError';
  }
}

/**
 * The authoritative quote for one attempt.
 *
 * The SERVER produces this; the client displays it and confirms it. Nothing
 * here reads a balance, an account or a history - a quote is a property of the
 * item and the target level, so two players are never quoted different prices
 * for the same attempt.
 */
export function upgradeQuote(input: {
  rarity: EquipmentRarity;
  currentLevel: number;
}): UpgradeQuote {
  const { rarity, currentLevel } = input;
  if (!Number.isSafeInteger(currentLevel) || currentLevel < 1) {
    throw new UpgradeNotAvailableError('current level is not a level');
  }
  if (currentLevel >= EQUIPMENT_MAX_LEVEL) {
    throw new UpgradeNotAvailableError('already at the maximum level');
  }
  const targetLevel = currentLevel + 1;
  const successBps = UPGRADE_SUCCESS_BPS[targetLevel];
  const baseGold = UPGRADE_GOLD_COST[targetLevel];
  if (successBps === undefined || baseGold === undefined) {
    throw new UpgradeNotAvailableError(`no rule for level ${targetLevel}`);
  }
  const multiplier = rarityCostMultiplierBps(rarity);
  // Exact in bigint, then rounded UP once: a price is never rounded in the
  // player's favour by accident, and the ceiling is a fraction of one Gold.
  const goldCost = ceilDiv(baseGold * BigInt(multiplier), 10_000n);
  const cashCost = ceilDiv(goldCost, PROVISIONAL_GOLD_PER_CASH);
  return {
    currentLevel,
    targetLevel,
    successBps,
    destroysOnFailure: attemptDestroysOnFailure(targetLevel),
    stabilizerDefinitionId: stabilizerIsUseful(targetLevel) ? QUANTUM_STABILIZER_ID : null,
    goldCost,
    cashCost,
    rarityMultiplierBps: multiplier,
    costVersion: UPGRADE_COST_VERSION,
  };
}

function ceilDiv(value: bigint, divisor: bigint): bigint {
  return (value + divisor - 1n) / divisor;
}

/**
 * What actually happened to the component. Every path is one of these four.
 *
 * `protected` is its own outcome rather than a flavour of `failed`: it consumed
 * a stabiliser and `failed` did not, and an audit trail that could not tell
 * them apart could not answer why an inventory is one item short.
 */
export const UPGRADE_OUTCOMES = ['succeeded', 'failed', 'protected', 'destroyed'] as const;
export type UpgradeOutcome = (typeof UPGRADE_OUTCOMES)[number];

/**
 * Resolves one attempt from a draw. Pure, so the rule is testable without a
 * database and identical on every path that reaches it.
 *
 * @param drawBelow10000 a uniform integer in `[0, 10000)`
 */
export function resolveUpgrade(input: {
  quote: UpgradeQuote;
  drawBelow10000: number;
  stabilizerApplied: boolean;
}): { outcome: UpgradeOutcome; newLevel: number; stabilizerConsumed: boolean } {
  const { quote, drawBelow10000, stabilizerApplied } = input;
  if (!Number.isSafeInteger(drawBelow10000) || drawBelow10000 < 0 || drawBelow10000 >= 10_000) {
    throw new RangeError('an upgrade draw must be an integer in [0, 10000)');
  }
  if (drawBelow10000 < quote.successBps) {
    // Success never consumes the stabiliser. It protected nothing, so it is
    // still in the player's inventory when the answer comes back.
    return { outcome: 'succeeded', newLevel: quote.targetLevel, stabilizerConsumed: false };
  }
  if (!quote.destroysOnFailure) {
    return { outcome: 'failed', newLevel: quote.currentLevel, stabilizerConsumed: false };
  }
  if (stabilizerApplied) {
    // Exactly one, and the component keeps the level it already had. It is not
    // a second chance at the upgrade - it is the absence of destruction.
    return { outcome: 'protected', newLevel: quote.currentLevel, stabilizerConsumed: true };
  }
  return { outcome: 'destroyed', newLevel: quote.currentLevel, stabilizerConsumed: false };
}

/** What to tell the player before they confirm, in the game's own language. */
export const UPGRADE_RISK_COPY = Object.freeze({
  safe: 'Uma falha consome o pagamento e mantém o item no nível atual.',
  destroys: 'Uma falha DESTRÓI o item permanentemente.',
  protected: 'Uma falha consome 1 Estabilizador Quântico e preserva o item.',
});
